import { useEffect, useRef } from "react";
import * as R from "ramda";
import cx from "classnames";

import { axisBottom } from "d3-axis";
import { select } from "d3-selection";
import { timeYear } from "d3-time";
import getYear from "date-fns/getYear";
import differenceInCalendarYears from "date-fns/fp/differenceInCalendarYears";

import { func, number, shape, string } from "prop-types";

const propTypes = {
  scale: func.isRequired, // scaleTime
  height: number.isRequired,
  margin: shape({
    left: number,
    top: number,
  }),
  className: string,
  tickSizeOuter: number,
  transitionDuration: number,
};

const defaultProps = {
  margin: {},
  transitionDuration: 250,
  tickSizeOuter: 6, // d3 default
};

const XAxisTrend = (props) => {
  const xAxisRef = useRef();
  const {
    scale,
    height,
    margin,
    className,
    tickSizeOuter,
    transitionDuration,
    ...otherProps
  } = props;

  useEffect(() => {
    const tickFormat = (date) => {
      const year = getYear(date);
      const width = R.last(scale.range());
      const years = differenceInCalendarYears(...scale.domain());

      // the space we want to 1 year label, the text is about 30px
      const yearWidth = 80;
      const canFit = width / yearWidth;
      const willFit = (every) => canFit > years / every;

      // every x years, hopefully we don't show more than 4000 years of data...
      const breakPoints = [1, 2, 5, 10, 15, 20, 30, 40, 50, 75, 100, 200, 400];
      const every = R.find(willFit, breakPoints);

      return year % every === 0 ? year : "";
    };

    const xAxis = axisBottom(scale)
      .tickPadding(15)
      .ticks(timeYear.every(1))
      .tickSizeOuter(tickSizeOuter)
      .tickFormat(tickFormat);

    select(xAxisRef.current)
      .transition()
      .duration(transitionDuration)
      .call(xAxis);

    // if the tick format is defined (ie, there is a label), make the tick line longer
    select(xAxisRef.current)
      .selectAll("g.tick line")
      // dumb, we need to disable the transition for the tick lines or else the
      // user can see it grow/shrink because this component is rerendered when the
      // parent change, eg, when the year hover line moves
      .transition()
      .duration(0)
      .attr("y2", (year) => xAxis.tickSizeInner() * (tickFormat(year) ? 2 : 1));
  }, [scale, tickSizeOuter, transitionDuration]);

  return (
    <g
      className={cx("x-axis", "x-axis--trend", className)}
      ref={xAxisRef}
      transform={`translate(${margin.left || 0}, ${
        height + (margin.top || 0)
      })`}
      {...otherProps}
    ></g>
  );
};

XAxisTrend.propTypes = propTypes;
XAxisTrend.defaultProps = defaultProps;
export default XAxisTrend;
